import { useActorRef } from "@xstate/react";
import { CommandInput } from "cmdk";
import { X } from "lucide-react";
import {
  memo,
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import { VStack } from "@/components/Layout";
import { Command, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/cn";

import {
  type SelectedPlaceOption,
  createPlaceSelectMachine,
} from "../-xstate/place-select-machine";
import type { Attribute, FieldRenderInputProps } from "../../types";

import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";
import { ReadOnlyField } from "./ReadOnlyField";

/**
 * 場所を検索するフィールドコンポーネント
 */
export const PlaceSelectField = memo((props: BaseFieldProps) => {
  const renderInput = useCallback(
    ({
      value: inputValue,
      onChange: handleChange,
      placeholder,
      disabled: isDisabled,
    }: FieldRenderInputProps) => {
      const machine = useMemo(() => createPlaceSelectMachine(), []);
      const stateMachineService = useActorRef(machine, {
        input: {
          selectedPlaceId: inputValue as string,
        },
      });

      stateMachineService.send({ type: "INIT" });
      // useSyncExternalStoreを使用して状態を一括取得
      const state = useSyncExternalStore(
        (callback) => {
          const subscription = stateMachineService.subscribe(callback);
          return () => subscription.unsubscribe();
        },
        () => stateMachineService.getSnapshot(),
      );

      if (!props.field) {
        return <div></div>;
      }

      const context = state.context;
      const selectedPlaceOption = context.selectedPlaceOption;
      const searchText = context.searchText;
      const searchResults = context.searchResults;

      const handlePlaceSelect = (
        placeId: string,
        option?: SelectedPlaceOption,
      ) => {
        if (option) {
          stateMachineService.send({
            type: "SELECT_PLACE",
            placeId,
            placeOption: option,
          });
          handleChange(option.id);
        }
      };

      const handlePlaceRemove = (placeId: string) => {
        stateMachineService.send({ type: "REMOVE_PLACE", placeId });
        handleChange(undefined);
      };

      const handleSearchChange = (text: string) => {
        stateMachineService.send({ type: "SEARCH_PLACE", text });
      };

      return (
        <VStack className="gap-2">
          <PlaceSelection
            options={searchText ? searchResults : []}
            value={selectedPlaceOption?.id ?? null}
            selectedPlaceOption={selectedPlaceOption}
            searchText={searchText}
            onSearchChange={handleSearchChange}
            onValueChange={handlePlaceSelect}
            onRemovePlace={handlePlaceRemove}
            disabled={isDisabled}
            readonly={props.readOnly || props.field.readonly}
            field={props.field}
            placeholder={placeholder}
          />
        </VStack>
      );
    },
    [props.field, props.readOnly],
  );
  return (
    <BaseField
      fieldId={props.fieldId}
      field={props.field}
      disabled={props.disabled}
      readOnly={false} //内部で切り替えるので
      layout={props.layout}
      className={props.className}
      data-testid={props["data-testid"]}
      renderInput={renderInput}
    />
  );
});

PlaceSelectField.displayName = "PlaceSelectField";

function PlaceSelection(props: {
  options: SelectedPlaceOption[];
  value: string | null;
  selectedPlaceOption: SelectedPlaceOption | null;
  searchText: string;
  onSearchChange?: (text: string) => void;
  onValueChange: (value: string, option?: SelectedPlaceOption) => void;
  onRemovePlace?: (placeId: string) => void;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  field: Attribute;
}) {
  // NOTE: 検索テキストの選択後にクリアするために保持
  const [searchText, setSearchText] = useState("");

  const handleSearchChange = useCallback(
    (text: string) => {
      props.onSearchChange?.(text);
      setSearchText(text);
    },
    [props],
  );

  const handleOnSelect = useCallback(
    (value: string) => {
      const option = props.options.find((opt) => opt.value === value);
      if (option && !option.disabled) {
        props.onValueChange(value, option);
        setSearchText("");
      }
    },
    [props],
  );

  const handleRemoveSelectedOption = useCallback(
    (value: string) => {
      props.onRemovePlace?.(value);
    },
    [props],
  );
  return (
    <>
      {props.readonly ? (
        <ReadOnlyField
          value={props.selectedPlaceOption?.label}
          field={props.field}
        />
      ) : (
        <Command shouldFilter={false}>
          <div className="flex min-h-[80px] gap-2 rounded-md border border-b border-neutral-muted-border px-3 py-2 shadow-xs">
            <div className="flex flex-1 flex-wrap items-start gap-1 py-2">
              {props.selectedPlaceOption ? (
                <div className="flex items-center gap-1 rounded-md bg-neutral-fill px-2 py-1">
                  <span className="text-xs font-medium">
                    {props.selectedPlaceOption.label}
                  </span>
                  {!props.disabled && (
                    <button
                      type="button"
                      onClick={() =>
                        props.selectedPlaceOption &&
                        handleRemoveSelectedOption(
                          props.selectedPlaceOption.value,
                        )
                      }
                      className="ml-1 hover:opacity-70"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ) : (
                <CommandInput
                  className="h-6 w-full text-xs outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={props.placeholder}
                  value={searchText}
                  onValueChange={handleSearchChange}
                  disabled={props.disabled}
                />
              )}
            </div>
          </div>
          {props.options.length ? (
            <CommandList
              className={cn(
                "gap-1 rounded-md border border-neutral-muted-border bg-neutral-fill p-1 shadow-xs",
              )}
            >
              {props.options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.value}
                  onSelect={
                    option.disabled
                      ? undefined
                      : () => handleOnSelect(option.value)
                  }
                  className={cn(
                    "cursor-pointer gap-1",
                    option.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{option.label}</span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          ) : (
            <></>
          )}
        </Command>
      )}
    </>
  );
}
